import { jsx as _jsx } from "react/jsx-runtime";
import { OktaContext } from '@okta/okta-react';
import React, { useEffect, Suspense, useContext } from 'react';
import { AccountAPI } from 'api/account';
import { PageLoading } from 'components/atoms/loading';
import { LoginPage } from 'components/organisms/login/login';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, TokenType } from 'services';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { logout } from 'utils/logout';
import { redirectToOAuth } from 'utils/oauth-redirect';
import { getTokenMeta } from 'utils/token';
import { LoginWithToken } from '../login-with-token';
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
// Restores the saved deep link when an already-authenticated user lands on /login (e.g. an
// existing Okta session restored on app boot, or the api-key bootstrap completing). Two reasons
// this is an effect + hard nav rather than the old `<Navigate to={ActiveTab.pop()}>`:
//   1. pop() must run ONCE — calling it in render (inside the `to=` prop) re-pops on the next
//      render to an empty value and falls back to origin → home.
//   2. Hard nav (window.location), not rocket-router SPA nav: post-#17308 an SPA replace here
//      lands before auth state propagates and the protected-route gate bounces back to home.
const RestoreSavedTab = ()=>{
    useEffect(()=>{
        const saved = ActiveTab.pop();
        let target = '/';
        try {
            target = saved ? new URL(saved).href.replace(window.location.origin, '') || '/' : '/';
        } catch (unused) {
            target = '/';
        }
        window.location.replace(target);
    }, []);
    return /*#__PURE__*/ _jsx(PageLoading, {});
};
export const Login = ()=>{
    var _params_get, _params_get1, _params_get2, _params_get3;
    var _accountMeta_okta, _accountMeta_okta1;
    // /login is a public route — it doesn't mount inside RouterShell, so the
    // static #rl-boot-skeleton overlay from public/index.html stays visible
    // (left-rail squares + content title placeholders) on top of the login
    // page or the magic-link <TokenError /> until something dismisses it.
    // Dismiss it here, matching the pattern in goodbye.tsx / render-portal.tsx.
    useDismissBootSkeleton();
    const oktaContext = useContext(OktaContext);
    const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
    const authState = oktaContext ? oktaContext.authState : {
        isAuthenticated: customIsAuthenticated
    };
    const { data: accountMeta } = useAccountMetaQuery();
    const params = useQueryParams();
    const token = (_params_get = params.get('token')) !== null && _params_get !== void 0 ? _params_get : '';
    const cognitoType = (_params_get1 = params.get('cognito')) !== null && _params_get1 !== void 0 ? _params_get1 : '';
    const extToken = (_params_get2 = params.get('rl-token')) !== null && _params_get2 !== void 0 ? _params_get2 : '';
    const overrideAuth = (_params_get3 = params.get('overrideAuth')) !== null && _params_get3 !== void 0 ? _params_get3 : false;
    const { isExpired, decoded } = getTokenMeta(token);
    useEffect(()=>{
        var _accountMeta_okta, _accountMeta_okta1;
        if ((accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta = accountMeta.okta) === null || _accountMeta_okta === void 0 ? void 0 : _accountMeta_okta.cookiesEnabled) && (accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta1 = accountMeta.okta) === null || _accountMeta_okta1 === void 0 ? void 0 : _accountMeta_okta1.clientId) && (authState === null || authState === void 0 ? void 0 : authState.isAuthenticated) === false) {
            // saveIfEmpty so we don't clobber the deep-link URL that UnauthenticatedRedirector
            // captured before nav()-ing here. If the user landed at /login directly (no prior deep
            // link), this captures /login and the restore later resolves to origin — harmless.
            ActiveTab.saveIfEmpty();
            redirectToOAuth(accountMeta.okta.clientId);
        }
    }, [
        accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta = accountMeta.okta) === null || _accountMeta_okta === void 0 ? void 0 : _accountMeta_okta.clientId,
        accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta1 = accountMeta.okta) === null || _accountMeta_okta1 === void 0 ? void 0 : _accountMeta_okta1.cookiesEnabled,
        authState === null || authState === void 0 ? void 0 : authState.isAuthenticated
    ]);
    if (decoded && isExpired) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Expired,
                email: decoded.emailId,
                resendEmail: ()=>AccountAPI.forgotAccount({
                        email: decoded.emailId
                    })
            })
        });
    }
    if (overrideAuth) {
        logout(`?token=${token}`);
        return /*#__PURE__*/ _jsx(PageLoading, {});
    }
    return !authState ? /*#__PURE__*/ _jsx(PageLoading, {}) : token || extToken ? // A token-consume owns its own post-login navigation: useLoginWithToken
    // (loginWithoutOtp) calls redirectToSavedTab, which pops ActiveTab once and restores
    // the deep link. This must take precedence over the authenticated branch below —
    // otherwise, once the consume flips the session to authenticated, that branch
    // re-renders and pops ActiveTab a SECOND time (now empty → falls back to origin),
    // overriding the restore and dropping the user on home. Keeping LoginWithToken
    // mounted while a token is present makes the hook the single restorer.
    /*#__PURE__*/ _jsx(LoginWithToken, {
        token: token || extToken || '',
        tokenType: extToken ? TokenType.External : cognitoType ? TokenType.Cognito : TokenType.Rocketlane
    }) : authState.isAuthenticated ? /*#__PURE__*/ _jsx(RestoreSavedTab, {}) : /*#__PURE__*/ _jsx(LoginPage, {});
};
